import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { FormNumericInput } from "components/form-fields";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { Modal, ModalSize } from "components/notifications";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import './Hierarchy.css';

export function HierarchyRepeatableModal({isOpen, component, isSection, onCancel, onSave}) {
    const [settings, setSettings] = useState({
        minimumNumberOfRepeats: 0,
        maximumNumberOfRepeats: 1,
        labelToAddMoreInstances: 'n/a'
    });
    const [isEditing, setIsEditing] = useState(false);

    const [isValid, errors, validate] = useValidation();
    
    useEffect(() => {
        if (isOpen && component.repeatability) {
            setSettings({...component.repeatability});
        }

        setIsEditing(!!component.repeatability);
    }, [isOpen]);

    useEffect(() => {
        validateSettings();
    }, [settings])

    function validateSettings() {
        const rules = [
            { fieldName: 'minimumNumberOfRepeats', required: true},
            { fieldName: 'maximumNumberOfRepeats', required: true},
        ];

        validate(rules, settings);
    }

    const renderComponentTitle = () => {
        if (component.title) {
            return component.title;
        }

        if (isSection) {
            return `Section ${component.componentNumber}`;
        }

        return `Question ${component.componentNumber}`;
    }

    const setMinNumberOfRepeats = (value) => {
        if (value >= settings.maximumNumberOfRepeats) {
            value = settings.maximumNumberOfRepeats - 1;
        }

        setSettings(prev => {
            return {
                ...prev,
                minimumNumberOfRepeats: value
            }
        });
    }

    const setMaxNumberOfRepeats = (value) => {
        if (value != null && value <= settings.minimumNumberOfRepeats) {
            value = settings.minimumNumberOfRepeats + 1;
        }

        setSettings(prev => {
            return {
                ...prev,
                maximumNumberOfRepeats: value
            }
        });
    }

    return <Modal
        isOpen={isOpen}
        title={`${isEditing ? "Edit" : "Add"} Repeat to ${renderComponentTitle()}`}
        showCloseButton={false}
        clickOutsideClose={false}
        size={ModalSize.SMALL}
    >
        <GridLayout numberOfColumns={2}>
            <FormNumericInput
                onValueChange={setMinNumberOfRepeats}
                headingText="Minimum number of repeats"
                selectedValue={settings.minimumNumberOfRepeats}
                min={0}
                max={settings.maximumNumberOfRepeats != null ? settings.maximumNumberOfRepeats - 1 : 100}
                dangerHelperText={errors.minimumNumberOfRepeats}
            />
            <FormNumericInput
                onValueChange={setMaxNumberOfRepeats}
                headingText="Maximum number of repeats"
                selectedValue={settings.maximumNumberOfRepeats}
                min={settings.minimumNumberOfRepeats != null ? settings.minimumNumberOfRepeats + 1 : 1}
                max={100}
                dangerHelperText={errors.maximumNumberOfRepeats}
            />
        </GridLayout>
        <div className="bp6-dialog-footer-actions">
            <Button
                text="Cancel"
                onClick={onCancel}
            ></Button>
            <ShowHide
                evaluator={isEditing}
                show={<Button
                    id="clear-repeatability"
                    onClick={() => onSave(null)}
                    text={'Clear Repeat'}
                    ></Button>}
            />
            <Button
                id="save-repeatability"
                onClick={() => onSave(settings)}
                intent={Intent.PRIMARY}
                disabled={!isValid}
                text={`${isEditing ? "Edit" : "Add"} Repeat`}
            ></Button>
        </div>

    </Modal>
}

HierarchyRepeatableModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isSection: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    component: PropTypes.object.isRequired
}